import GeofenceRequester from '@cobliteam/api-lib/src/dashboard/geofence/GeofenceRequesterV3';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';

// Mirrors modules/geofences/src/pages/GeofencesPage/Geofences.tsx's hasFewGeofences (also reused,
// with the same threshold, by modules/routing's ServicesDeliveriesSuggestionsBanner).
export const FEW_GEOFENCES_MAX = 2;
export const hasFewGeofences = (geofenceCount: number): boolean => geofenceCount < FEW_GEOFENCES_MAX;

const STALE_TIME_MS = 10 * 60 * 1000;

// Lazy instantiation (same pattern as useEntity/useGeofenceV3) to avoid circular dependency issues
// and to keep Environment access out of module load time.
const getGeofenceRequester = () => new GeofenceRequester({ host: Environment.getInstance().getEnvironment().geofenceApiHost });

/**
 * Cheap fleet-wide geofence-count check.
 *
 * AE-2569 wrote it as the "flag OR under-threshold" fallback for geofence suggestions, which never
 * worked: `useGeofenceRecommendations` ANDs `poisRecommendations` internally, so a fleet without
 * the flag never fetched and the OR downstream of it could not enable anyone.
 *
 * Its job now is narrower and real: it is the DISPLAY gate for the geofence nudge in the floating
 * stack, mirroring the 0-1 group gate on the groups side. Without it a flagged fleet would carry a
 * permanent "see place suggestions" row in the corner no matter how many places it already has.
 *
 * Only fetches when `enabled`.
 */
const useHasFewGeofences = (fleetId: string | undefined, enabled = true) => {
  const { data, isLoading } = useQuery({
    queryKey: ['AI_RECOMMENDATIONS_GEOFENCE_COUNT', fleetId],
    queryFn: async () => {
      const response = await getGeofenceRequester().getGeofences({ filters: { page: 0, unpaged: true } });
      return response.content;
    },
    enabled: !!fleetId && enabled,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return { isEligible: hasFewGeofences(data?.length ?? 0), isLoading };
};

export default useHasFewGeofences;
